import React, { useState, useMemo } from 'react';
import { ArrowLeft, Server, Globe, Database, Code as Code2, Users, Loader as Loader2, RotateCcw, TriangleAlert as AlertTriangle, ListTodo } from 'lucide-react';
import { toast } from 'sonner';
import type { XCloudServer } from '@/types/xcloud';
import {
  useServerDatabases, useServerCronJobs, useServerMonitoring,
  useServerPhpVersions, useServerSudoUsers, useServerTasks,
  useServerSites, useRebootServer,
} from '@/hooks/useXcloud';

type Tab = 'overview' | 'sites' | 'databases' | 'cron-jobs' | 'php' | 'users' | 'monitoring' | 'tasks';

interface Props {
  server: XCloudServer;
  workspaceId: string;
  canManage: boolean;
  onClose: () => void;
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
        active
          ? 'border-sky-500 text-sky-600 dark:text-sky-400'
          : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
      }`}
    >
      {children}
    </button>
  );
}

function DataRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-6 px-4 py-3 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground shrink-0 w-40">{label}</span>
      <span className="text-sm text-foreground text-right flex-1 min-w-0 break-all">{value ?? '—'}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const labelMap: Record<string, string> = {
    active: 'Connected',
    provisioned: 'Connected',
    provisioning: 'Provisioning',
    inactive: 'Inactive',
    failed: 'Failed',
  };
  const colorMap: Record<string, string> = {
    active: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    provisioned: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    provisioning: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
    inactive: 'bg-muted text-muted-foreground',
    failed: 'bg-red-500/10 text-red-500',
  };
  return (
    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${colorMap[status] ?? 'bg-muted text-muted-foreground'}`}>
      {labelMap[status] ?? status}
    </span>
  );
}

export function ServerDetailDrawer({ server, workspaceId, canManage, onClose }: Props) {
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [showRebootConfirm, setShowRebootConfirm] = useState(false);

  const { data: sitesData, isLoading: sitesLoading } = useServerSites(workspaceId, activeTab === 'sites' ? server.uuid : undefined);
  const { data: dbData, isLoading: dbLoading } = useServerDatabases(workspaceId, activeTab === 'databases' ? server.uuid : undefined);
  const { data: cronData, isLoading: cronLoading } = useServerCronJobs(workspaceId, activeTab === 'cron-jobs' ? server.uuid : undefined);
  const { data: monData, isLoading: monLoading } = useServerMonitoring(workspaceId, activeTab === 'monitoring' ? server.uuid : undefined);
  const { data: phpData, isLoading: phpLoading } = useServerPhpVersions(workspaceId, activeTab === 'php' ? server.uuid : undefined);
  const { data: usersData, isLoading: usersLoading } = useServerSudoUsers(workspaceId, activeTab === 'users' ? server.uuid : undefined);
  const { data: tasksData, isLoading: tasksLoading } = useServerTasks(workspaceId, activeTab === 'tasks' ? server.uuid : undefined);

  const monitoringItems = useMemo(() => {
    if (!monData?.data) return [];
    return [
      { label: 'CPU Usage', value: monData.data.cpu_usage, suffix: '%' },
      { label: 'Memory Usage', value: monData.data.memory_usage, suffix: '%' },
      { label: 'Disk Usage', value: monData.data.disk_usage, suffix: '%' },
      { label: 'Response Time', value: monData.data.response_time, suffix: 'ms' },
    ].filter(m => m.value !== undefined);
  }, [monData]);

  const rebootMutation = useRebootServer(workspaceId);

  const handleReboot = async () => {
    try {
      await rebootMutation.mutateAsync(server.uuid);
      toast.success('Server reboot initiated');
      setShowRebootConfirm(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reboot server');
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'sites', label: 'Sites' },
    { id: 'databases', label: 'Databases' },
    { id: 'cron-jobs', label: 'Cron Jobs' },
    { id: 'php', label: 'PHP Versions' },
    { id: 'users', label: 'Sudo Users' },
    { id: 'monitoring', label: 'Monitoring' },
    { id: 'tasks', label: 'Tasks' },
  ];

  return (
    <div className="w-full">
      {/* Back bar */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onClose}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Hosting
        </button>
      </div>

      {/* Page header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-sky-500/10 flex items-center justify-center shrink-0">
            <Server className="w-6 h-6 text-sky-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{server.name}</h1>
            <div className="flex items-center gap-3 mt-1">
              <StatusBadge status={server.status} />
              {server.ip_address && (
                <span className="text-sm text-muted-foreground font-mono">{server.ip_address}</span>
              )}
              {(server.location || server.region) && (
                <span className="text-sm text-muted-foreground capitalize">{server.location || server.region}</span>
              )}
            </div>
          </div>
        </div>

        {canManage && (
          <button
            onClick={() => setShowRebootConfirm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-muted text-sm font-medium text-foreground transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reboot
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-0 border-b border-border mb-6 overflow-x-auto">
        {tabs.map(t => (
          <TabBtn key={t.id} active={activeTab === t.id} onClick={() => setActiveTab(t.id)}>
            {t.label}
          </TabBtn>
        ))}
      </div>

      {/* Tab content */}
      <div className="w-full">
        {activeTab === 'overview' && (
          <div className="bg-card rounded-xl border border-border divide-y divide-border">
            <DataRow label="Provider" value={server.provider} />
            <DataRow label="Location" value={server.location || server.region} />
            <DataRow label="IP Address" value={server.ip_address} />
            <DataRow label="Stack" value={server.stack?.toUpperCase()} />
            <DataRow label="PHP Version" value={server.php_version} />
            <DataRow label="Ubuntu" value={server.ubuntu_version} />
            <DataRow label="Monitoring" value={
              server.monitoring === true ? 'Enabled'
              : server.monitoring === false ? 'Disabled'
              : undefined
            } />
            <DataRow label="Created" value={server.created_at ? new Date(server.created_at).toLocaleDateString() : undefined} />
          </div>
        )}

        {activeTab === 'sites' && (
          sitesLoading ? <LoadingState /> :
          !sitesData?.data?.length ? <EmptyState label="No sites on this server" /> :
          <div className="space-y-2">
            {sitesData.data.map((s, i) => (
              <div key={s.uuid || s.name || i} className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border">
                <Globe className="w-4 h-4 text-sky-500 shrink-0" />
                <span className="text-sm text-foreground flex-1 truncate">{s.title || s.name || s.domain_name || s.domain}</span>
                <span className="text-xs text-muted-foreground capitalize px-2 py-0.5 rounded-full bg-muted">{s.status}</span>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'databases' && (
          dbLoading ? <LoadingState /> :
          !dbData?.data?.length ? <EmptyState label="No databases found" /> :
          <div className="space-y-2">
            {dbData.data.map((db, i) => (
              <div key={db.uuid || db.name || i} className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border">
                <Database className="w-4 h-4 text-sky-500 shrink-0" />
                <span className="text-sm text-foreground flex-1">{db.name}</span>
                {db.user && <span className="text-xs text-muted-foreground">{db.user}</span>}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'cron-jobs' && (
          cronLoading ? <LoadingState /> :
          !cronData?.data?.length ? <EmptyState label="No cron jobs configured" /> :
          <div className="space-y-2">
            {cronData.data.map((job, i) => (
              <div key={job.uuid || i} className="p-4 rounded-xl bg-card border border-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">{job.frequency || 'Custom'}</span>
                  <span className="text-xs text-muted-foreground capitalize">{job.status}</span>
                </div>
                <code className="text-xs text-muted-foreground font-mono bg-muted px-2 py-1 rounded">{job.command}</code>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'monitoring' && (
          monLoading ? <LoadingState /> :
          !monData?.data ? <EmptyState label="No monitoring data available" /> :
          <div className="space-y-4">
            {monitoringItems.map(m => (
              <div key={m.label} className="p-4 rounded-xl bg-card border border-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">{m.label}</span>
                  <span className="text-sm font-semibold text-foreground">{m.value}{m.suffix}</span>
                </div>
                {m.suffix === '%' && (
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-sky-500 transition-all"
                      style={{ width: `${Math.min(100, m.value as number)}%` }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'php' && (
          phpLoading ? <LoadingState /> :
          !phpData?.data?.length ? <EmptyState label="No PHP versions found" /> :
          <div className="space-y-2">
            {phpData.data.map((v, i) => (
              <div key={`${v.version}-${i}`} className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border">
                <Code2 className="w-4 h-4 text-sky-500 shrink-0" />
                <span className="text-sm text-foreground flex-1">PHP {v.version}</span>
                {v.is_active && (
                  <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium px-2 py-0.5 rounded-full bg-emerald-500/10">Active</span>
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'users' && (
          usersLoading ? <LoadingState /> :
          !usersData?.data?.length ? <EmptyState label="No sudo users found" /> :
          <div className="space-y-2">
            {usersData.data.map((u, i) => (
              <div key={u.uuid || u.username || i} className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border">
                <Users className="w-4 h-4 text-sky-500 shrink-0" />
                <span className="text-sm text-foreground flex-1">{u.username}</span>
                {u.created_at && (
                  <span className="text-xs text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</span>
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'tasks' && (
          tasksLoading ? <LoadingState /> :
          !tasksData?.data?.length ? <EmptyState label="No tasks found" /> :
          <div className="space-y-2">
            {tasksData.data.map((t, i) => (
              <div key={t.uuid || i} className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border">
                <ListTodo className="w-4 h-4 text-sky-500 shrink-0" />
                <span className="text-sm text-foreground flex-1 truncate">{t.name || t.type}</span>
                <span className="text-xs text-muted-foreground capitalize">{t.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reboot confirm modal */}
      {showRebootConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowRebootConfirm(false)} />
          <div className="relative w-full max-w-sm bg-card rounded-2xl border border-border shadow-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="font-semibold text-foreground">Reboot Server?</p>
                <p className="text-xs text-muted-foreground">All sites will be temporarily unavailable</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowRebootConfirm(false)}
                className="flex-1 px-4 py-2.5 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReboot}
                disabled={rebootMutation.isPending}
                className="flex-1 px-4 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {rebootMutation.isPending ? <><Loader2 className="w-4 h-4 animate-spin" /> Rebooting...</> : 'Confirm Reboot'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}
